import {CoworkBarePanel} from '../admin/CoworkFormPanel';
import React, { useLayoutEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { fetchUserLevels } from '../../services/userLevelService';
import { registerUser } from '../../services/userService';
import { createPersonal, updatePersonal, fetchPersonal, Personal } from '../../services/personalService';
import ImageUpload from '../Upload/ImageUpload'; // Asegúrate de que la ruta sea correcta
import { useModalFocus } from '../../hooks/useModalFocus';
import { useModalLifecycle } from '../../hooks/useModalLifecycle';

interface AddPersonnelModalProps {
    isOpen: boolean;
    onClose: () => void;
    onAddPersonnel: (nuevoPersonal: Personal) => void;
}

interface UserLevel {
    id: number;
    nombre: string;
    perfil_asociado: 'cliente' | 'personal';
    activo: boolean;
}

interface FormData {
    first_name: string;
    last_name: string;
    email: string;
    password: string;
    estado: boolean;
    tipo_nivel_usuario: string;
    cargo: string;
    telefono: string;
    pin_caja: string;
    foto: File | null;
}

/** Estado visual de UNA apertura, etiquetado con su clave de identidad. */
interface View {
    key: number;
    form: FormData;
    /** Catálogo de niveles cargado por ESTA apertura; ``null`` hasta que su carga termine con éxito. */
    levels: UserLevel[] | null;
    loading: boolean;
    error: string | null;
}

const EMPTY_FORM: FormData = {
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    estado: true,
    tipo_nivel_usuario: '',
    cargo: '',
    telefono: '',
    pin_caja: '',
    foto: null,
};

const freshView = (key: number): View => ({ key, form: EMPTY_FORM, levels: null, loading: false, error: null });

/** Solo niveles de personal ACTIVOS son elegibles para el alta. */
const isAssignableLevel = (level: UserLevel) => level.perfil_asociado === 'personal' && level.activo === true;

const TITLE_ID = 'add-personnel-title';

/**
 * Alta de personal: registra el usuario y crea (o completa) su ficha de
 * personal, con foto opcional, en varias peticiones encadenadas.
 *
 * Identidad = apertura (``isOpen``), fijada EN RENDER por
 * ``useModalLifecycle``: el estado visual (formulario, catálogo de niveles,
 * error, loading) se lee DERIVADO por clave, así en cada apertura el
 * formulario arranca vacío y el selector vacío y deshabilitado ya en el
 * primer commit, y una carga o respuesta de la apertura anterior nunca
 * repuebla, habilita, entrega ni cierra la nueva. El envío toma un bloqueo
 * SÍNCRONO (dos submits en el mismo turno = un registro) y verifica la
 * identidad antes y después de CADA ``await``: cerrar, desmontar o reabrir a
 * mitad no ejecuta callbacks, errores ni loading de la apertura vieja ni
 * continúa con los pasos siguientes.
 *
 * Cerrar (botón, Cancelar, backdrop o Escape) es terminal e idempotente.
 * Modal accesible: ``role="dialog"`` con ``aria-modal`` y título asociado;
 * foco inicial en Nombre; Escape cierra; Tab/Shift+Tab confinados; al cerrar
 * o desmontar devuelve el foco al opener si sigue en el DOM.
 */
const AddPersonnelModal: React.FC<AddPersonnelModalProps> = ({ isOpen, onClose, onAddPersonnel }) => {
    const [viewState, setViewState] = useState<View>(() => freshView(0));
    const panelRef = useRef<HTMLDivElement>(null);
    const firstNameRef = useRef<HTMLInputElement>(null);

    const lifecycle = useModalLifecycle({
        identity: isOpen ? 'open' : null,
        onClose,
        // Suelta el estado anterior (contraseña y PIN incluidos) en cuanto cambia la apertura.
        onReset: key => setViewState(freshView(key)),
    });
    const { identityKey, rendered } = lifecycle;
    const view = viewState.key === identityKey ? viewState : freshView(identityKey);
    const patch = (changes: Partial<Omit<View, 'key'>>) => setViewState(prev => ({
        ...(prev.key === identityKey ? prev : freshView(identityKey)),
        ...changes,
        key: identityKey,
    }));
    const patchForm = (changes: Partial<FormData>) => setViewState(prev => {
        const base = prev.key === identityKey ? prev : freshView(identityKey);
        return { ...base, key: identityKey, form: { ...base.form, ...changes } };
    });
    const formData = view.form;
    const levelsReady = view.levels !== null;
    const userLevels: UserLevel[] = view.levels ?? [];

    // Carga de niveles de ESTA apertura (fail-closed: solo su éxito habilita el selector).
    useLayoutEffect(() => {
        if (!rendered) return;
        const generation = lifecycle.invalidate();
        fetchUserLevels()
            .then((levels: UserLevel[]) => {
                if (!lifecycle.isLive(generation)) return;
                patch({ levels: levels.filter(isAssignableLevel) });
            })
            .catch(() => {
                // Sin console.error: el error arrastra la petición/respuesta. La UI avisa.
                if (lifecycle.isLive(generation)) patch({ error: 'No se pudieron cargar los niveles de usuario' });
            });
        // La clave de identidad ya codifica isOpen; el resto son refs/estables.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [identityKey]);

    useModalFocus({ active: rendered, panelRef, initialFocusRef: firstNameRef, onEscape: lifecycle.close });

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;
        if (type === 'checkbox') {
            const checked = (e.target as HTMLInputElement).checked;
            patchForm({ [name]: checked } as Partial<FormData>);
        } else {
            patchForm({ [name]: name === 'pin_caja' ? value.replace(/\D/g, '').slice(0, 6) : value } as Partial<FormData>);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        // Cerrado, en la frontera de una apertura nueva o ya guardando: no se envía.
        if (!lifecycle.canWrite()) return;
        patch({ error: null });

        if (!formData.first_name.trim() || !formData.last_name.trim() || !formData.email.trim() || !formData.password.trim()) {
            patch({ error: 'Nombre, apellido, email y contraseña son obligatorios' });
            return;
        }
        if (formData.pin_caja && !/^\d{3,6}$/.test(formData.pin_caja)) {
            patch({ error: 'El PIN de caja debe tener de 3 a 6 dígitos.' });
            return;
        }
        // Solo se envía un nivel que esté en el catálogo VIGENTE (personal y
        // activo); cualquier otro valor no es enviable.
        const selectedLevel = formData.tipo_nivel_usuario
            ? userLevels.find(level => String(level.id) === formData.tipo_nivel_usuario)
            : undefined;
        if (formData.tipo_nivel_usuario && !selectedLevel) {
            patch({ error: 'El nivel de usuario seleccionado ya no está disponible.' });
            return;
        }

        // Bloqueo síncrono de escritura y generación de ESTA apertura: se
        // comprueba tras cada ``await`` antes de seguir o tocar estado.
        const generation = lifecycle.beginWrite();
        if (generation === null) return;
        patch({ loading: true });

        try {
            // 1. Crear usuario
            const userData = await registerUser(
                formData.email,
                formData.first_name,
                formData.last_name,
                formData.password,
                formData.estado,
                selectedLevel ? selectedLevel.id : null
            );
            if (!lifecycle.isLive(generation)) return;

            const userId = userData.id;

            // 2. Verificar si ya existe registro de personal para este usuario
            const personalList = await fetchPersonal();
            if (!lifecycle.isLive(generation)) return;
            const personalExistente = personalList.find(p => p.usuario?.id === userId);

            let personalFinal: Personal;

            if (personalExistente) {
                // Actualizar personal existente
                personalFinal = await updatePersonal(personalExistente.id, {
                    cargo: formData.cargo || null,
                    telefono: formData.telefono || null,
                    pin_caja: formData.pin_caja || undefined,
                });
            } else {
                // Crear nuevo personal
                personalFinal = await createPersonal({
                    usuario: userId,
                    cargo: formData.cargo || null,
                    telefono: formData.telefono || null,
                    pin_caja: formData.pin_caja || undefined,
                });
            }
            if (!lifecycle.isLive(generation)) return;

            // 3. Si hay foto, actualizar con ella
            if (formData.foto) {
                personalFinal = await updatePersonal(personalFinal.id, {
                    foto: formData.foto,
                });
                if (!lifecycle.isLive(generation)) return;
            }

            // Se libera ANTES de cerrar (close invalida la generación y el
            // finally ya no aplicaría) por si el padre tarda en desmontar.
            lifecycle.endWrite(generation);
            patch({ loading: false });
            onAddPersonnel(personalFinal);
            lifecycle.close();
        } catch {
            // No se registra ni se muestra el error: la petición incluye contraseña y
            // PIN de caja y un AxiosError conserva config/request/response con esos datos.
            if (lifecycle.isLive(generation)) patch({ error: 'Error al crear el personal. Verifica los datos.' });
        } finally {
            if (lifecycle.isLive(generation)) {
                lifecycle.endWrite(generation);
                patch({ loading: false });
            }
        }
    };

    if (!rendered) return null;

    const { loading, error } = view;

    return (
        <div data-luma-overlay="true"
            className="fixed inset-0 z-50 flex items-center justify-center p-4 pt-[max(1rem,env(safe-area-inset-top))] pb-[max(1rem,env(safe-area-inset-bottom))]"
            data-testid="add-personnel-overlay"
        >
            <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm" data-testid="add-personnel-backdrop" onClick={lifecycle.close} />
            <CoworkBarePanel data-luma-dialog="true"
                ref={panelRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby={TITLE_ID}
                tabIndex={-1}
                className="luma-personnel-dialog bg-white p-4 sm:p-6 rounded-xl w-full max-w-2xl min-w-0 border border-gray-100 shadow-xl relative overflow-y-auto max-h-[calc(100vh-2rem)] supports-[height:100dvh]:max-h-[calc(100dvh-2rem)] [overflow-wrap:anywhere] focus:outline-none"
            >
                <div className="flex justify-between items-start gap-3 mb-4">
                    <h2 id={TITLE_ID} className="text-xl font-bold text-gray-900">Agregar Nuevo Personal</h2>
                    <div className="flex items-center gap-4 min-w-0">
                        {error && <div className="text-red-500 text-sm" role="alert">{error}</div>}
                        <button
                            type="button"
                            onClick={lifecycle.close}
                            aria-label="Cerrar alta de personal"
                            className="shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
                        >
                            <X className="w-6 h-6" aria-hidden="true" />
                        </button>
                    </div>
                </div>

                <form onSubmit={handleSubmit}><div className="luma-form-intro"><span>01</span><div><h3>Información del registro</h3><p>Completa los campos del formulario.</p></div></div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4" data-testid="add-personnel-fields">
                        {/* Nombre */}
                        <div className="min-w-0" data-testid="add-personnel-field">
                            <label htmlFor="add-personnel-first-name" className="block text-sm font-medium text-gray-700 mb-2">
                                Nombre <span className="text-red-500">*</span>
                            </label>
                            <input
                                id="add-personnel-first-name"
                                ref={firstNameRef}
                                type="text"
                                name="first_name"
                                value={formData.first_name}
                                onChange={handleChange}
                                required
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>

                        {/* Apellido */}
                        <div className="min-w-0" data-testid="add-personnel-field">
                            <label htmlFor="add-personnel-last-name" className="block text-sm font-medium text-gray-700 mb-2">
                                Apellido <span className="text-red-500">*</span>
                            </label>
                            <input
                                id="add-personnel-last-name"
                                type="text"
                                name="last_name"
                                value={formData.last_name}
                                onChange={handleChange}
                                required
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>

                        {/* Email */}
                        <div className="min-w-0" data-testid="add-personnel-field">
                            <label htmlFor="add-personnel-email" className="block text-sm font-medium text-gray-700 mb-2">
                                Email <span className="text-red-500">*</span>
                            </label>
                            <input
                                id="add-personnel-email"
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                required
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>

                        {/* Contraseña */}
                        <div className="min-w-0" data-testid="add-personnel-field">
                            <label htmlFor="add-personnel-password" className="block text-sm font-medium text-gray-700 mb-2">
                                Contraseña <span className="text-red-500">*</span>
                            </label>
                            <input
                                id="add-personnel-password"
                                type="password"
                                name="password"
                                value={formData.password}
                                onChange={handleChange}
                                required
                                minLength={6}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>

                        {/* Nivel de usuario (solo personal) */}
                        <div className="min-w-0" data-testid="add-personnel-field">
                            <label htmlFor="add-personnel-level" className="block text-sm font-medium text-gray-700 mb-2">
                                Nivel de usuario
                            </label>
                            {/* Deshabilitado hasta que la carga vigente termine con éxito. */}
                            <select
                                id="add-personnel-level"
                                name="tipo_nivel_usuario"
                                value={formData.tipo_nivel_usuario}
                                onChange={handleChange}
                                disabled={!levelsReady}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                            >
                                <option value="">Seleccionar nivel</option>
                                {userLevels.map(level => (
                                    <option key={level.id} value={level.id}>
                                        {level.nombre}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Cargo */}
                        <div className="min-w-0" data-testid="add-personnel-field">
                            <label htmlFor="add-personnel-cargo" className="block text-sm font-medium text-gray-700 mb-2">Cargo</label>
                            <input
                                id="add-personnel-cargo"
                                type="text"
                                name="cargo"
                                value={formData.cargo}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>

                        {/* Teléfono */}
                        <div className="min-w-0" data-testid="add-personnel-field">
                            <label htmlFor="add-personnel-telefono" className="block text-sm font-medium text-gray-700 mb-2">Teléfono</label>
                            <input
                                id="add-personnel-telefono"
                                type="tel"
                                name="telefono"
                                value={formData.telefono}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>

                        {/* PIN especial de caja */}
                        <div className="min-w-0" data-testid="add-personnel-field">
                            <label htmlFor="add-pin-caja" className="block text-sm font-medium text-gray-700 mb-2">PIN de caja</label>
                            <input id="add-pin-caja" type="password" inputMode="numeric" autoComplete="new-password" name="pin_caja" value={formData.pin_caja} onChange={handleChange} minLength={3} maxLength={6} pattern="[0-9]{3,6}" placeholder="3 a 6 dígitos" aria-describedby="add-pin-caja-help" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                            <p id="add-pin-caja-help" className="text-xs text-gray-500 mt-1">Se guarda como hash más una copia cifrada que solo el administrador puede ver en el detalle del personal.</p>
                        </div>
                    </div>

                    {/* Foto de perfil (opcional) */}
                    <div className="mb-4">
                        <ImageUpload setImagen={(file) => patchForm({ foto: file })} />
                    </div>

                    {/* Toggle para Usuario activo */}
                    <div className="mb-4">
                        <div className="flex items-center justify-between gap-3 bg-gray-50 p-3 rounded-lg">
                            <div className="min-w-0">
                                <span id="add-personnel-estado-label" className="text-sm font-medium text-gray-700">
                                    Usuario activo
                                </span>
                                <p className="text-xs text-gray-500 mt-1">
                                    {formData.estado ? 'El usuario estará habilitado' : 'El usuario estará inhabilitado'}
                                </p>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer shrink-0">
                                <input
                                    type="checkbox"
                                    name="estado"
                                    className="sr-only"
                                    aria-labelledby="add-personnel-estado-label"
                                    checked={formData.estado}
                                    onChange={handleChange}
                                />
                                <div
                                    className={`w-10 h-6 rounded-full transition-colors ${
                                        formData.estado ? 'bg-[#3564db]' : 'bg-gray-300'
                                    }`}
                                >
                                    <div
                                        className={`absolute top-1 left-1 bg-white w-4 h-4 rounded-full shadow-sm transform transition-transform ${
                                            formData.estado ? 'translate-x-5' : 'translate-x-0'
                                        }`}
                                    ></div>
                                </div>
                            </label>
                        </div>
                    </div>

                    <div className="flex flex-wrap justify-end gap-3 mt-6" data-testid="add-personnel-actions">
                        <button
                            type="button"
                            onClick={lifecycle.close}
                            className="px-4 py-2 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors font-medium"
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className={`px-4 py-2 text-sm bg-[#3564db] text-white rounded-lg transition-colors font-medium flex items-center ${
                                loading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-[#8f1f64]'
                            }`}
                        >
                            {loading ? (
                                <span className="flex items-center">
                                    <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24" aria-hidden="true">
                                        <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                            fill="none"
                                        />
                                        <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                        />
                                    </svg>
                                    Guardando...
                                </span>
                            ) : (
                                'Guardar Personal'
                            )}
                        </button>
                    </div>
                </form>
            </CoworkBarePanel>
        </div>
    );
};

export default AddPersonnelModal;


